/**
 * Service composition root.
 *
 * The one place that decides which concrete services the application uses. Every
 * component receives services from here (via the router's context), never by
 * importing a concrete implementation. That is what lets a mock be swapped for a
 * Firebase-backed service without touching a single component.
 *
 * Phase 2 wired the Firebase-backed auth service when a Firebase configuration is
 * present (`config.firebase`), and the in-memory mock otherwise — the prototype
 * and the accessibility/e2e build have no backend and stay on the mock. Phase 3
 * adds the Firebase profile service on the same switch, so a configured build now
 * administers real, server-encrypted profiles. Retrieval remains mocked until
 * Phase 4 replaces it here.
 *
 * One Firebase context is shared by every service: initialising the SDK twice
 * would produce two auth instances, and the profile callables would then be sent
 * a token from the wrong one.
 */

import { createFirebaseContext } from "./firebase/app.js";
import { startAppCheck } from "./firebase/app-check.js";
import { createFirebaseAuthService } from "./firebase/auth.js";
import { createFirebaseProfileService } from "./firebase/profiles.js";
import { createFirebaseRetrievalService } from "./firebase/retrieval.js";
import { createFirebaseBillingService } from "./firebase/billing.js";
import { createFirebaseAccountService } from "./firebase/account.js";
import { createMockAuthService } from "./mock/auth.js";
import { createMockProfileService } from "./mock/profiles.js";
import { createMockRetrievalService } from "./mock/retrieval.js";
import { createMockBillingService } from "./mock/billing.js";
import { createMockAccountService } from "./mock/account.js";

/**
 * Build the service set for the given configuration.
 *
 * @param {import("../config/environment.js").KinCodeConfig} config
 * @returns {import("./interfaces.js").Services}
 */
export function createServices(config) {
  if (!config.firebase) {
    return {
      auth: createMockAuthService(),
      profiles: createMockProfileService(),
      retrieval: createMockRetrievalService(),
      billing: createMockBillingService(),
      account: createMockAccountService(),
    };
  }

  const context = createFirebaseContext(config.firebase, { useEmulators: config.useEmulators });
  return {
    auth: createFirebaseAuthService(context),
    profiles: createFirebaseProfileService(context),
    retrieval: createFirebaseRetrievalService(context),
    billing: createFirebaseBillingService(context),
    account: createFirebaseAccountService(context),
    // Deliberately a callback rather than something started here. Initialising
    // App Check at the composition root would load reCAPTCHA on every page,
    // including public retrieval — see firebase/app-check.js.
    startAttestation: () => startAppCheck(context, config.appCheckSiteKey ?? undefined),
  };
}
